import {confidenceStatusColor} from 'src/theme/semantic-colors'

import {
    needsDriverNotification,
    notifiesOnlyDrivers,
    pendingDriverNotificationCount,
} from './allocation'
import {driversToNotifyLabel} from './driver-send-status'
import type {TimelineJobRow} from './job-rows'
import type {AllocationStatus} from './types'

export type ConfidenceLevel = 'unconfirmed' | 'ready' | 'committed' | 'blocked'

export type ActionOwner = 'dispatcher' | 'system'

export type NextAction = 'assignment' | 'notification' | 'none'

export type OperationalState = {
    confidence: ConfidenceLevel
    actionOwner: ActionOwner
    nextAction: NextAction
    confidenceLabel: string
    ownershipLabel: string
}

const CONFIDENCE_LABELS: Record<ConfidenceLevel, string> = {
    unconfirmed: 'Needs action',
    ready: 'Notify drivers',
    committed: 'Dispatched',
    blocked: 'Needs action',
}

/** Derives observable allocation and notification work for one board row. */
export function deriveOperationalState(
    job: TimelineJobRow,
    allocationStatus: AllocationStatus,
): OperationalState {
    if (allocationStatus !== 'ready') {
        return {
            confidence: 'unconfirmed',
            actionOwner: 'dispatcher',
            nextAction: 'assignment',
            confidenceLabel: CONFIDENCE_LABELS.unconfirmed,
            ownershipLabel: 'Assign crew and equipment',
        }
    }

    const unnotifiedDrivers = job.allocations.filter(needsDriverNotification).length
    if (unnotifiedDrivers > 0) {
        return {
            confidence: 'ready',
            actionOwner: 'dispatcher',
            nextAction: 'notification',
            confidenceLabel: CONFIDENCE_LABELS.ready,
            ownershipLabel: driversToNotifyLabel(
                unnotifiedDrivers,
                notifiesOnlyDrivers(
                    job.allocations,
                    job.requirements ?? job.unassignedJob?.requirements,
                ),
            ),
        }
    }

    return {
        confidence: 'committed',
        actionOwner: 'system',
        nextAction: 'none',
        confidenceLabel: CONFIDENCE_LABELS.committed,
        ownershipLabel: 'Allocation complete',
    }
}

export function confidenceColor(confidence: ConfidenceLevel): string {
    return confidenceStatusColor(confidence)
}

export function unnotifiedDriverCountForRow(job: TimelineJobRow): number {
    return job.unassignedJob
        ? pendingDriverNotificationCount(job.unassignedJob)
        : job.allocations.filter(needsDriverNotification).length
}
